import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeOff, ShieldCheck, KeyRound, Copy, Loader2, LogOut, Plus, Ban, Check, X, ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { API } from "../lib/pro";
import { SalesSection, AlertSettings } from "../components/admin/Sales";
import { PromoSection } from "../components/admin/Promos";

const TOKEN = "lw.adminToken";
const call = async (path, method = "GET", body) => {
  const r = await fetch(`${API}/api/admin${path}`, {
    method, headers: { "Content-Type": "application/json", Authorization: `Bearer ${sessionStorage.getItem(TOKEN) || ""}` },
    body: body ? JSON.stringify(body) : undefined,
  });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw Object.assign(new Error(typeof j.detail === "string" ? j.detail : `Request failed (${r.status})`), { status: r.status });
  return j;
};
const copy = (t) => { navigator.clipboard.writeText(t); toast("Copied"); };
const when = (d) => (d ? new Date(d).toLocaleString("en-SG") : "—");

const Login = ({ onIn }) => {
  const [pw, setPw] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { const { token } = await call("/login", "POST", { password: pw }); setPw(""); sessionStorage.setItem(TOKEN, token); onIn(); }
    catch (x) { toast.error(x.message); }
    setBusy(false);
  };
  return (
    <form onSubmit={submit} className="lw-card w-full max-w-sm p-6 space-y-4" data-testid="admin-login-form">
      <div className="flex items-center gap-2 font-semibold"><ShieldCheck className="h-5 w-5 text-primary" />PaperLite admin</div>
      <div className="relative">
        <Input type={show ? "text" : "password"} autoFocus autoComplete="current-password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Admin password" className="pr-10" data-testid="admin-password-input" />
        <button type="button" onClick={() => setShow((v) => !v)} aria-label={show ? "Hide password" : "Show password"} title={show ? "Hide password" : "Show password"}
          className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 rounded-md flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-accent" data-testid="admin-password-toggle">
          {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
      <Button type="submit" className="w-full" disabled={!pw || busy} data-testid="admin-login-btn">{busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Sign in</Button>
    </form>
  );
};

const Keys = ({ keys, reload }) => {
  const [note, setNote] = useState("");
  const [fresh, setFresh] = useState("");
  const make = async (e) => {
    e.preventDefault();
    try { const { key } = await call("/licenses", "POST", { note }); setFresh(key); setNote(""); reload(); }
    catch (x) { toast.error(x.message); }
  };
  const download = () => {
    const blob = new Blob([`PaperLite Pro — complimentary licence\n\nKey: ${fresh}\n\nOpen PaperLite › Get PaperLite Pro › enter the key.\nThis key activates on ONE device only and cannot be reused.\n`], { type: "text/plain" });
    const a = Object.assign(document.createElement("a"), { href: URL.createObjectURL(blob), download: `${fresh}.txt` });
    a.click();
  };
  return (
    <section className="lw-card p-5 space-y-4" data-testid="admin-keys">
      <h2 className="font-semibold flex items-center gap-2"><KeyRound className="h-4 w-4" />Free single-use licence keys</h2>
      <form onSubmit={make} className="flex flex-wrap gap-2">
        <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Who is it for? (optional)" className="flex-1 min-w-[180px]" data-testid="admin-key-note-input" />
        <Button type="submit" data-testid="admin-create-key-btn"><Plus className="h-4 w-4 mr-1" />Create key</Button>
      </form>
      {fresh && (
        <div className="rounded-lg border bg-emerald-500/10 p-3 flex flex-wrap items-center gap-2" data-testid="admin-new-key">
          <span className="font-mono text-sm flex-1" data-testid="admin-new-key-value">{fresh}</span>
          <Button size="sm" variant="outline" onClick={() => copy(fresh)} data-testid="admin-copy-key-btn"><Copy className="h-3.5 w-3.5 mr-1" />Copy</Button>
          <Button size="sm" onClick={download} data-testid="admin-download-key-btn">Download .txt</Button>
        </div>
      )}
      <div className="divide-y text-sm">
        {keys.map((k) => (
          <div key={k.key} className="py-2 flex flex-wrap items-center gap-x-3 gap-y-1" data-testid={`admin-key-${k.key}`}>
            <span className="font-mono">{k.key}</span>
            <span className="text-muted-foreground flex-1 truncate">{k.note}</span>
            <span className={`text-xs rounded px-1.5 py-0.5 ${k.revoked ? "bg-destructive/10 text-destructive" : k.activated ? "bg-muted" : "bg-emerald-500/10 text-emerald-700"}`}>{k.revoked ? "Revoked" : k.activated ? `Used ${when(k.activated_at)}` : "Unused"}</span>
            {!k.revoked && <Button size="sm" variant="ghost" className="h-7" onClick={async () => { await call(`/licenses/${k.key}/revoke`, "POST"); reload(); }} data-testid={`admin-revoke-${k.key}`}><Ban className="h-3.5 w-3.5" /></Button>}
          </div>
        ))}
        {!keys.length && <p className="text-muted-foreground py-2">No keys yet.</p>}
      </div>
    </section>
  );
};

const Orders = ({ orders, reload }) => {
  const act = async (id, a) => {
    try { const r = await call(`/orders/${id}/${a}`, "POST"); if (r.license_key) { copy(r.license_key); toast.success(`Approved · key copied: ${r.license_key}`); } reload(); }
    catch (x) { toast.error(x.message); }
  };
  return (
    <section className="lw-card p-5 space-y-3" data-testid="admin-orders">
      <h2 className="font-semibold">PayNow / PayPal QR orders</h2>
      <p className="text-xs text-muted-foreground">Check your bank or PayPal app for the payment, then approve. The licence key is emailed to the buyer automatically, and their app picks it up when they press “Check status”. Each key works on 3 devices.</p>
      <div className="divide-y text-sm">
        {orders.map((o) => (
          <div key={o.order_id} className="py-2 flex flex-wrap items-center gap-x-3 gap-y-1" data-testid={`admin-order-${o.order_id}`}>
            <span className="font-mono">{o.order_id}</span><span>{o.email}</span><span className={`text-[10px] font-semibold uppercase rounded px-1.5 py-0.5 ${o.method === "paypal" ? "bg-[#003087]/10 text-[#003087]" : "bg-[#7B2D8E]/10 text-[#7B2D8E]"}`}>{o.method === "paypal" ? "PayPal" : "PayNow"}</span><span className="text-muted-foreground">Ref {o.reference} · S${Number(o.amount).toFixed(2)} · {when(o.created_at)}</span>
            {o.status === "approved" && <span className="text-xs text-muted-foreground" data-testid={`admin-order-meta-${o.order_id}`}>{o.activations}/3 devices · {o.emailed ? "emailed" : "not emailed"}</span>}
            <span className="flex-1" />
            {o.status === "pending" ? (
              <>
                <Button size="sm" onClick={() => act(o.order_id, "approve")} data-testid={`admin-approve-${o.order_id}`}><Check className="h-3.5 w-3.5 mr-1" />Approve</Button>
                <Button size="sm" variant="ghost" onClick={() => act(o.order_id, "reject")} data-testid={`admin-reject-${o.order_id}`}><X className="h-3.5 w-3.5" /></Button>
              </>
            ) : o.status === "approved" ? (
              <span className="text-xs flex items-center gap-1">
                <button className={`font-mono underline ${o.revoked ? "line-through text-muted-foreground" : ""}`} onClick={() => copy(o.license_key)}>{o.license_key}</button>
                {o.revoked ? <span className="text-destructive">Revoked</span> : <Button size="sm" variant="ghost" className="h-7" onClick={async () => { await call(`/licenses/${o.license_key}/revoke`, "POST"); reload(); }} data-testid={`admin-order-revoke-${o.order_id}`}><Ban className="h-3.5 w-3.5" /></Button>}
              </span>
            ) : <span className="text-xs">Rejected</span>}
          </div>
        ))}
        {!orders.length && <p className="text-muted-foreground py-2">No orders yet.</p>}
      </div>
    </section>
  );
};

export default function Admin() {
  const [authed, setAuthed] = useState(!!sessionStorage.getItem(TOKEN));
  const [keys, setKeys] = useState([]);
  const [orders, setOrders] = useState([]);
  const load = useCallback(async () => {
    try { const [k, o] = await Promise.all([call("/licenses"), call("/orders")]); setKeys(k); setOrders(o); }
    catch (x) { if (x.status === 401) { sessionStorage.removeItem(TOKEN); setAuthed(false); } else toast.error(x.message); }
  }, []);
  useEffect(() => { if (authed) load(); }, [authed, load]);
  return (
    <div className="min-h-screen bg-muted/40 p-5 md:p-10" data-testid="admin-page">
      <div className="max-w-3xl mx-auto space-y-5">
        <div className="flex items-center justify-between">
          <Link to="/" className="text-sm text-muted-foreground flex items-center gap-1 hover:text-foreground"><ArrowLeft className="h-4 w-4" />Documents</Link>
          {authed && <Button size="sm" variant="ghost" onClick={() => { sessionStorage.removeItem(TOKEN); setAuthed(false); }} data-testid="admin-logout-btn"><LogOut className="h-4 w-4 mr-1" />Sign out</Button>}
        </div>
        {authed ? <><SalesSection call={call} /><AlertSettings call={call} /><PromoSection call={call} /><Keys keys={keys} reload={load} /><Orders orders={orders} reload={load} /></> : <div className="flex justify-center pt-16"><Login onIn={() => setAuthed(true)} /></div>}
      </div>
    </div>
  );
}
